import type { FolderUser, FolderUserUpdate } from '../../types';
import type { FolderPermissionsPut } from '../../api';
import { useCallback, useContext, useEffect, useState } from 'react';
import { hideLoader, showLoader } from '~/common';
import { ErrorPage, PreviewDrawerContext } from '~/features/common';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import { useGetFolderPermissions, useSetFolderPermissionUsers, useGetUserAccessLevel } from '../../api';
import { FolderPermissionsDrawer } from '../FolderPermissionsDrawer';

export type FolderPermissionsDrawerContainerProps = {
  folderId: string;
};

export function FolderPermissionsDrawerContainer({ folderId }: FolderPermissionsDrawerContainerProps) {
  const updatePermissions = useSetFolderPermissionUsers(folderId).mutate;

  const handlePermissionsSave = (data: FolderPermissionsPut) => updatePermissions(data);

  const [users, setUsers] = useState([]);
  const [isDirty, setIsDirty] = useState(false);

  const handlePermissionUpdate = (data: FolderUserUpdate) => {
    if (!isDirty) {
      setIsDirty(true);
    }

    const permissionIdx = users.findIndex(
      (permission) =>
        permission.userId === data.userId && permission.groupId === data.groupId && permission.inheritedFolder === null,
    );

    if (data.accessLevel === 'remove') {
      const newPermissions = [...users.slice(0, permissionIdx), ...users.slice(permissionIdx + 1)];
      setUsers(newPermissions);
    } else {
      const newPermissions = users.slice();
      newPermissions[permissionIdx] = { ...newPermissions[permissionIdx], ...data };
      setUsers(newPermissions);
    }
  };

  const handlePermissionAdd = (permission: FolderUser) => {
    const duplicate = users.some(
      (user) =>
        user.userId === permission.userId && user.groupId === permission.groupId && user.inheritedFolder === null,
    );

    if (duplicate) {
      return;
    }

    if (!isDirty) {
      setIsDirty(true);
    }

    const newPermissions = [...users, permission].sort((a, b) => a.displayName.localeCompare(b.displayName));

    setUsers(newPermissions);
  };
  const permissionsQuery = useGetFolderPermissions(folderId);
  const accessQuery = useGetUserAccessLevel(folderId);

  const { setIsOpen } = useContext(PreviewDrawerContext);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const handleCancel = useCallback(() => {
    handleClose();
    setUsers(permissionsQuery.data.permissions); // reset state
  }, [handleClose, permissionsQuery.data]);

  useEffect(() => {
    if (permissionsQuery.isLoading || accessQuery.isLoading) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [permissionsQuery.isLoading, accessQuery.isLoading]);

  useEffect(() => {
    if (permissionsQuery.status === 'success') {
      setUsers(permissionsQuery.data.permissions);
    }
  }, [permissionsQuery.status, permissionsQuery.data]);

  if (permissionsQuery.isLoading || accessQuery.isLoading) return null;

  if (permissionsQuery.error || accessQuery.error)
    return <ErrorPage status={permissionsQuery.error.status || accessQuery.error.status} />;

  let canEdit = window.getCurrentUser().admin;
  if (isFeatureEnabled('allow_full_editors_to_manage_folder_permissions')) {
    canEdit ||= accessQuery.data.includes('full_editor');
  }

  return (
    <FolderPermissionsDrawer
      onClose={handleClose}
      onCancel={handleCancel}
      folderName={permissionsQuery.data.folderName}
      folderUsers={users}
      onUpdate={handlePermissionUpdate}
      onAdd={handlePermissionAdd}
      onSave={handlePermissionsSave}
      confirmCancel={isDirty}
      readOnly={!canEdit}
    />
  );
}
